import { useEffect, useRef } from "react";

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.classList.add("opacity-100", "translate-y-0");
          section.classList.remove("opacity-0", "translate-y-8");
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(section);

    const items = section.querySelectorAll<HTMLElement>(".fade-up-item");
    const itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            itemObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    items.forEach((item) => itemObserver.observe(item));

    return () => {
      observer.disconnect();
      itemObserver.disconnect();
    };
  }, []);

  return (
    <section
      id="apropos"
      ref={sectionRef}
      className="pt-0 pb-0 relative opacity-0 translate-y-8 transition-all duration-700 bg-background-50"
    >
      <div className="pt-14 md:pt-20 pb-6 md:pb-8">
        <div className="max-w-[980px] mx-auto px-6 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="lg:order-1">
              <h2 className="text-3xl md:text-5xl font-playfair font-bold text-terracotta mb-3 md:mb-4 fade-up-item">
                LA - LOU
              </h2>
              <h3 className="text-base md:text-xl text-foreground-600 font-semibold mb-6 md:mb-8 fade-up-item delay-1 tracking-wide uppercase">
                Fondatrice & Directrice
              </h3>
              <div className="space-y-4 md:space-y-5 text-foreground-800 leading-[1.75] text-[15px] md:text-base max-w-[540px]">
                <p className="fade-up-item delay-1">
                  Fondée par Lou Vanessa Matthey, Lalou Maestra imagine, développe et coordonne des{" "}
                  <strong className="text-terracotta font-bold">événements publics, gastronomiques et corporate en Suisse romande</strong>.
                </p>

                <p className="fade-up-item delay-2">
                  Forte de <strong className="text-terracotta font-bold">plus de 10 ans d&apos;expérience</strong>, Lou accompagne ses clients de la{" "}
                  <strong className="text-terracotta font-bold">réflexion stratégique à la réalisation opérationnelle</strong>, avec une approche alliant{" "}
                  <strong className="text-terracotta font-bold">créativité, cohérence et maîtrise du terrain</strong>.
                </p>

                <p className="fade-up-item delay-3">
                  Festivals, manifestations publiques, événements gastronomiques ou projets sur mesure : chaque réalisation est pensée dans sa globalité afin d&apos;offrir une{" "}
                  <strong className="text-terracotta font-bold">expérience fluide, marquante et adaptée à son public</strong>.
                </p>

                <p className="fade-up-item delay-4">
                  Grâce à un <strong className="text-terracotta font-bold">solide réseau de partenaires de confiance</strong> et à une{" "}
                  <strong className="text-terracotta font-bold">présence active sur le terrain</strong>, Lalou Maestra transforme les ambitions, les envies et les opportunités en{" "}
                  <strong className="text-terracotta font-bold">événements concrets et mémorables</strong>.
                </p>

                <img
                  src="https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/22d44b98962deca6e6e0f2db1194b373.png"
                  alt="Signature"
                  className="h-36 md:h-40 mt-10 opacity-80 fade-up-item delay-6"
                />
              </div>
            </div>
            <div className="lg:order-2 fade-up-item self-start">
              <div className="relative h-[420px] md:h-[560px] lg:h-[640px]">
                <img
                  src="https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/3ea94325581b355f4f1d2db38498ba2b.jfif"
                  alt="Lou Vanessa Matthey"
                  className="w-full h-full rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.08)] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}